import { EntryType, type EntryItemType, type TreeNode } from '@morph-mapper/types';
import type { ChatIntent, Suggestion } from '@/store';
import { fieldPath } from './open-field';

const MAX_VALUE = 60;
const MAX_LINES = 120;
// How many of a field's AI suggestions the assistant sees, best first.
const MAX_SUGGESTIONS = 3;
// Longer than other values: the assistant names a suggestion by this text.
const MAX_SUGGESTION_TEXT = 120;
// How many options of a list field the assistant sees.
const MAX_OPTIONS = 30;
// The API cuts the template at 20000 characters, from the end. Staying under
// that keeps the last fields, and the open field, in the prompt.
const MAX_CHARS = 18000;

/** The values a list field accepts, from its zod enum validation. */
export const listValues = (entry: TreeNode): string[] | null => {
  const def = (entry.validation as { _def?: { typeName?: string; values?: unknown } } | undefined)
    ?._def;
  return def?.typeName === 'ZodEnum' && Array.isArray(def.values) ? (def.values as string[]) : null;
};

// How a field can get its value, in the words of the kinds of change.
const WAYS: Record<EntryItemType, string> = {
  [EntryType.Cell]: 'column',
  [EntryType.Simple]: 'value',
  [EntryType.Boolean]: 'yes/no value',
  [EntryType.Graph]: 'graph',
  [EntryType.Code]: 'code',
  [EntryType.Internal]: 'internal value',
};

/**
 * What a field accepts, when it is limited: the options of a list field, and
 * the only ways a field can get its value. The card checks the same.
 */
const rulesOf = (node: TreeNode): string => {
  const rules: string[] = [];
  const options = listValues(node);
  if (options) {
    const shown = options.slice(0, MAX_OPTIONS).map((option) => JSON.stringify(option));
    const more = options.length > MAX_OPTIONS ? ` and ${options.length - MAX_OPTIONS} more` : '';
    rules.push(`options: ${shown.join(', ')}${more}`);
  }
  const allowed = (node as { allowedTypes?: EntryItemType[] }).allowedTypes ?? [];
  if (allowed.length > 0) {
    rules.push(`can only be: ${allowed.map((type) => WAYS[type] ?? type).join(', ')}`);
  }
  return rules.map((rule) => ` | ${rule}`).join('');
};

/**
 * An AI suggestion's value as the assistant sees it in the template mapping,
 * and so the text a change names it by. Undefined for an empty value.
 */
export const suggestionText = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (text === undefined) return undefined;
  return text.length > MAX_SUGGESTION_TEXT ? `${text.slice(0, MAX_SUGGESTION_TEXT)}…` : text;
};

/** A field's AI suggestion values, best first, to put after its line. */
const suggestionsOf = (list: Suggestion[] | undefined): string => {
  const values = [...(list ?? [])]
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .map((suggestion) => suggestionText(suggestion.value))
    .filter((value, index, all): value is string => value !== undefined && all.indexOf(value) === index)
    .slice(0, MAX_SUGGESTIONS);
  return values.length > 0 ? ` | AI suggestions: ${values.map((v) => JSON.stringify(v)).join(', ')}` : '';
};

const preview = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '(empty)';

  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (text === undefined) return '(empty)';

  return text.length > MAX_VALUE ? `${text.slice(0, MAX_VALUE)}…` : text;
};

/** How a field gets its value — the part a user asks for help with. */
const source = (node: TreeNode): string => {
  switch (node.type) {
    case EntryType.Cell:
      return node.column === undefined ? 'cell, no column set' : `cell, column ${node.column}`;
    case EntryType.Graph:
      return node.graphId ? 'graph' : 'graph, not built yet';
    case EntryType.Code:
      return 'code';
    case EntryType.Simple:
      return 'literal';
    case EntryType.Boolean:
      return 'boolean';
    case EntryType.Internal:
      return 'internal';
    default:
      return node.type;
  }
};

/**
 * A compact, readable picture of the template for the chat assistant.
 *
 * The assistant is asked to help map fields, but the API only ever sent it the
 * source document — never the mapping. Without this it can say "column C looks
 * like a debtor reference"; it cannot say "debtor is still empty".
 */
export const summariseTemplate = (
  entries: Record<string, TreeNode>,
  rootId: string | undefined,
  suggestions: Record<string, Suggestion[]> = {},
  openEntryId?: string
): string | undefined => {
  if (!rootId || !entries[rootId]) return undefined;

  const lines: string[] = [];
  const shown = new Set<string>();
  let used = 0;
  let full = false;

  /** Adds a line while there is room for it, and says whether there was. */
  const add = (line: string): boolean => {
    if (!full && (lines.length >= MAX_LINES || used + line.length > MAX_CHARS)) full = true;
    if (full) return false;
    lines.push(line);
    used += line.length + 1;
    return true;
  };

  /** A field's line: its path, source, what it computes to and what it accepts. */
  const fieldLine = (node: TreeNode, path: string) =>
    `${path} — ${source(node)} → ${preview(node.computed)}${rulesOf(node)}${suggestionsOf(suggestions[node.id])}`;

  const walk = (id: string, path: string) => {
    const node = entries[id];
    if (full || !node) return;
    const here = path ? `${path}.${node.name}` : node.name;

    if (node.type === EntryType.Map) {
      add(`${here} — group (${node.outputType})`);
      for (const childId of Object.keys(node.children.forwardMap)) {
        walk(childId, here);
      }
      return;
    }
    if (add(fieldLine(node, here))) shown.add(id);
  };

  walk(rootId, '');

  if (lines.length === 0) return undefined;
  if (full) {
    lines.push('… (more fields, truncated to save space)');
    // The field the user has open is the one they ask about, so it is there
    // even when the template is too long to show whole.
    const open = openEntryId ? entries[openEntryId] : undefined;
    if (open && open.type !== EntryType.Map && !shown.has(open.id)) {
      lines.push(fieldLine(open, fieldPath(entries, open.id) ?? open.name));
    }
  }

  return lines.join('\n');
};

/**
 * The template summary to send with a chat message. Only the mapping assistant
 * gets it: the other cards don't need the template, so its values stay in the
 * browser.
 */
export const templateContextFor = (
  intent: ChatIntent | null,
  entries: Record<string, TreeNode>,
  rootId: string | undefined,
  suggestions: Record<string, Suggestion[]> = {},
  openEntryId?: string
): string | undefined =>
  intent === 'mapping-assistant'
    ? summariseTemplate(entries, rootId, suggestions, openEntryId)
    : undefined;
